import { useCallback, useEffect, useState } from "react";
import { Download, ImageIcon, Play, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { LegacyArtifact } from "~/entities/chat";
import { AssetProvenanceAction } from "~/entities/asset";
import { useActiveWorkspace } from "~/entities/workspace";
import { useInViewport } from "~/shared/hooks/useInViewport";
import { downloadFromUrl } from "~/shared/lib/download";
import { cn } from "~/shared/lib/utils";
import { Skeleton } from "~/shared/ui/skeleton";
import { useChatUIStore } from "~/shared/store/useChatUIStore";

interface MessageArtifactsProps {
    artifacts: LegacyArtifact[];
    isPendingStorage?: boolean;
    // Нижняя генерация ленты: грузится приоритетно и показывает, что идёт
    // загрузка, — см. VirtualMessageList и ArtifactCard.
    isPriorityMedia?: boolean;
    /** Imported media use server-generated previews in the grid, never full originals. */
    isImported?: boolean;
}

// 1 артефакт — на полную ширину; 2+ — две колонки.
function gridColsFor(count: number): string {
    if (count <= 1) return "grid-cols-1";
    return "grid-cols-2";
}

export function MessageArtifacts({
    artifacts,
    isPendingStorage = false,
    isPriorityMedia = false,
    isImported = false,
}: MessageArtifactsProps) {
    // Клик по артефакту открывает единую галерею всех сгенерированных объектов
    // чата (image + video в одной ленте) на этом объекте — листается стрелками.
    // Сама галерея рендерится в ChatShell (ChatArtifactGallery),
    // источник — серверный список артефактов чата; связь через UI-store по URL.
    const openGallery = useChatUIStore((s) => s.openGallery);

    if (artifacts.length === 0) return null;

    return (
        <div className={cn("mt-2 grid gap-3", gridColsFor(artifacts.length))}>
            {artifacts.map((artifact) => (
                <ArtifactCard
                    key={artifact.id}
                    artifact={artifact}
                    onOpen={() => openGallery({ id: artifact.id, url: artifact.artifactUrl })}
                    isPendingStorage={isPendingStorage || artifact.isPendingStorage}
                    isPriorityMedia={isPriorityMedia}
                    isImported={isImported}
                />
            ))}
        </div>
    );
}

interface ArtifactCardProps {
    artifact: LegacyArtifact;
    onOpen: () => void;
    isPendingStorage: boolean;
    isPriorityMedia: boolean;
    isImported: boolean;
}

function ArtifactCard({
    artifact,
    onOpen,
    isPendingStorage,
    isPriorityMedia,
    isImported,
}: ArtifactCardProps) {
    const { t } = useTranslation("chat");
    const { activeWorkspaceId } = useActiveWorkspace();
    const previewUrl = isImported ? artifact.previewUrl : artifact.artifactUrl;
    const canLoadMedia = artifact.artifactType !== "audio" && previewUrl !== null;
    // Нативный `loading="lazy"` не даёт нам состояния ожидания и не умеет
    // повторить сорвавшийся запрос. В длинной импортированной истории это
    // выглядело как случайные чёрные плитки, поэтому жизненный цикл медиа
    // контролируется карточкой (см. ArtifactImage/Video ниже).
    const [mediaState, setMediaState] = useState<"idle" | "loading" | "ready" | "error">("idle");
    const [attempt, setAttempt] = useState(0);
    // В импортированной истории может быть сотня медиа. Оставляем в браузере
    // только небольшое окно рядом с viewport: иначе Firefox держит старые
    // video/image-запросы и новые плитки ждут их в очереди до таймаута.
    const { ref: viewportRef, isInView } = useInViewport<HTMLDivElement>({
        rootMargin: "240px 0px",
        once: false,
    });
    const shouldMount = isPriorityMedia || isInView;

    const markLoading = useCallback(
        () => setMediaState((state) => (state === "ready" ? state : "loading")),
        [],
    );
    const markReady = useCallback(() => setMediaState("ready"), []);
    const markError = useCallback(() => setMediaState("error"), []);

    // Не каждый оборванный запрос заканчивается browser `error` event. Не
    // оставляем такой объект вечным пустым плейсхолдером: даём человеку retry.
    useEffect(() => {
        if (mediaState !== "loading") return;
        const timeout = window.setTimeout(() => setMediaState("error"), 30_000);
        return () => window.clearTimeout(timeout);
    }, [attempt, mediaState]);

    // Размонтированный ролик больше не должен продолжать свой 30-секундный
    // отсчёт. При следующем попадании в окно он начнёт чистую попытку, а не
    // встретит пользователя старой ошибкой.
    useEffect(() => {
        if (shouldMount && canLoadMedia) {
            markLoading();
        } else {
            setMediaState("idle");
        }
    }, [attempt, canLoadMedia, markLoading, shouldMount]);

    const retryMedia = (e: React.MouseEvent) => {
        e.stopPropagation();
        setMediaState("idle");
        setAttempt((value) => value + 1);
    };

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isPendingStorage) return;
        void downloadFromUrl(artifact.artifactUrl).catch((err) => {
            console.error("download failed", err);
        });
    };

    const downloadTitle = isPendingStorage ? t("messages.artifacts.savingTooltip") : "Download";

    const downloadButton = (
        <button
            type="button"
            onClick={handleDownload}
            disabled={isPendingStorage}
            aria-label="Download"
            title={downloadTitle}
            className={cn(
                "absolute top-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-zinc-200 opacity-0 backdrop-blur-md transition-all group-hover:opacity-100 hover:bg-black/70 hover:text-white focus-visible:opacity-100",
                isPendingStorage && "cursor-not-allowed opacity-60 hover:bg-black/50 hover:text-zinc-200",
            )}
        >
            <Download className="h-4 w-4" />
        </button>
    );

    const provenanceButton = (
        <AssetProvenanceAction
            assetId={artifact.id}
            workspaceId={activeWorkspaceId ?? undefined}
            className="absolute top-2 right-12 z-10 size-8 bg-black/50 text-zinc-200 opacity-0 backdrop-blur-md transition-all group-hover:opacity-100 hover:bg-black/70 hover:text-white focus-visible:opacity-100"
        />
    );

    // Аудио — невысокая карточка с плеером, без медиа-бокса.
    if (artifact.artifactType === "audio") {
        return (
            <div className="group relative overflow-hidden rounded-xl border border-white/5 bg-zinc-900/40">
                <div className="flex w-full items-center justify-center p-3">
                    <audio src={artifact.artifactUrl} controls className="w-full" />
                </div>
                {downloadButton}
                {provenanceButton}
            </div>
        );
    }

    // Видео/картинка — единый фикс. 16:9 бокс: одинаковая высота и ширина у всех
    // сообщений ленты. `object-contain` вписывает медиа целиком (вертикальные
    // 9:16 — с серыми полями по бокам), ничего не обрезая. Высота бокса задана
    // до загрузки медиа → нет layout shift, лента не прыгает при догрузке даже
    // без кэша (клик открывает полный размер в галерее).
    return (
        <div
            ref={viewportRef}
            className="group relative aspect-[16/9] overflow-hidden rounded-xl border border-white/5 bg-zinc-900/40"
        >
            {canLoadMedia && mediaState !== "ready" && mediaState !== "error" && (
                <Skeleton className="absolute inset-0 rounded-none" />
            )}
            {artifact.artifactType === "video" && !isImported && previewUrl ? (
                <ArtifactVideo
                    src={previewUrl}
                    attempt={attempt}
                    onReady={markReady}
                    onError={markError}
                    shouldMount={shouldMount}
                />
            ) : previewUrl ? (
                <ArtifactImage
                    src={previewUrl}
                    attempt={attempt}
                    isPriorityMedia={isPriorityMedia}
                    onReady={markReady}
                    onError={markError}
                    shouldMount={shouldMount}
                />
            ) : (
                <ArtifactPreviewUnavailable artifactType={artifact.artifactType} />
            )}
            {mediaState === "error" && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-zinc-950/70 px-3 text-center">
                    <span className="text-xs text-zinc-300">{t("messages.artifacts.loadFailed")}</span>
                    <button
                        type="button"
                        onClick={retryMedia}
                        className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:outline-none"
                    >
                        <RefreshCw className="size-3.5" aria-hidden />
                        {t("messages.artifacts.retryMedia")}
                    </button>
                </div>
            )}
            {artifact.artifactType === "video" && mediaState !== "error" && (
                <span
                    className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
                    aria-hidden
                >
                    <span className="flex size-12 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm">
                        <Play className="size-6 translate-x-px fill-current" />
                    </span>
                </span>
            )}
            <button
                type="button"
                onClick={onOpen}
                aria-label="Open"
                className="absolute inset-0 cursor-zoom-in bg-transparent transition-colors hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
            />
            {isPendingStorage && (
                <div className="absolute top-2 left-2 z-10 rounded-md bg-amber-500/15 px-2 py-1 text-xs font-medium text-amber-200 backdrop-blur-sm">
                    {t("messages.artifacts.savingBadge")}
                </div>
            )}
            {downloadButton}
            {provenanceButton}
        </div>
    );
}

function ArtifactPreviewUnavailable({ artifactType }: { artifactType: LegacyArtifact["artifactType"] }) {
    const isVideo = artifactType === "video";
    const Icon = isVideo ? Play : ImageIcon;

    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-950/50 text-zinc-500">
            <span className="flex size-12 items-center justify-center rounded-full bg-white/5">
                <Icon className="size-5" aria-hidden />
            </span>
            <span className="text-xs">
                {isVideo ? "Video preview is preparing" : "Image preview is preparing"}
            </span>
        </div>
    );
}

function retryUrl(src: string, attempt: number): string {
    if (attempt === 0) return src;
    try {
        const url = new URL(src);
        // B2 держит ответ в browser cache до семи дней. Новый URL нужен только
        // после явного retry, чтобы не переиспользовать cached failed response.
        url.searchParams.set("sntz_media_retry", String(attempt));
        return url.toString();
    } catch {
        return src;
    }
}

// Не полагаемся на browser-native `loading="lazy"`: IntersectionObserver
// создаёт элемент только в контролируемом окне рядом с viewport, а eager
// относится лишь к уже смонтированным плиткам.
function ArtifactImage({
    src,
    attempt,
    isPriorityMedia = false,
    onReady,
    onError,
    shouldMount,
}: {
    src: string;
    attempt: number;
    isPriorityMedia?: boolean;
    onReady: () => void;
    onError: () => void;
    shouldMount: boolean;
}) {
    return (
        <div className="absolute inset-0">
            {shouldMount && (
                <img
                    key={attempt}
                    src={retryUrl(src, attempt)}
                    alt=""
                    loading="eager"
                    fetchPriority={isPriorityMedia ? "high" : undefined}
                    onLoad={onReady}
                    onError={onError}
                    className="absolute inset-0 h-full w-full object-contain"
                />
            )}
        </div>
    );
}

// Видео использует то же контролируемое окно загрузки. `metadata` недостаточен
// для показа первого кадра в Firefox: ему разрешён только заголовок контейнера,
// а `loadeddata` так и не приходит. Небольшое число одновременно смонтированных
// роликов позволяет безопасно попросить браузер подгрузить кадр (`auto`).
function ArtifactVideo({
    src,
    attempt,
    onReady,
    onError,
    shouldMount,
}: {
    src: string;
    attempt: number;
    onReady?: () => void;
    onError: () => void;
    shouldMount: boolean;
}) {
    return (
        <div className="absolute inset-0">
            {shouldMount && (
                <video
                    key={attempt}
                    src={`${retryUrl(src, attempt)}#t=0.1`}
                    muted
                    playsInline
                    preload="auto"
                    onLoadedData={onReady}
                    onCanPlay={onReady}
                    onError={onError}
                    className="absolute inset-0 h-full w-full object-contain"
                />
            )}
        </div>
    );
}
